import { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { AuthShell, EASE_OUT_STRONG } from '@/components/auth/AuthShell'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/hooks/useAuth'
import { ArrowRight, LogOut } from 'lucide-react'

const REF_KEY = 'legallady_affiliate_ref'
const REF_META_KEY = 'legallady_affiliate_ref_meta'

export default function AffiliateRedirectPage() {
  const { code } = useParams()
  const navigate = useNavigate()
  const { isAuthenticated, isLoading, onboardingComplete, logout } = useAuth()

  useEffect(() => {
    if (code) {
      localStorage.setItem(REF_KEY, code)
      localStorage.setItem(REF_META_KEY, JSON.stringify({
        code,
        firstTouchAt: new Date().toISOString(),
        expiresAt: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString(),
      }))
    }

    if (isLoading) return
    if (!isAuthenticated || !onboardingComplete) {
      navigate('/onboarding/step/1', { replace: true })
    }
  }, [code, isAuthenticated, isLoading, navigate, onboardingComplete])

  const startCustomerSignup = async () => {
    localStorage.removeItem('app_onboarding_data')
    localStorage.removeItem('app_onboarding_step')
    localStorage.removeItem('app_onboarding_paused')
    localStorage.removeItem('app_onboarding_complete')
    await logout()
    window.location.href = '/onboarding/step/1'
  }

  if (isAuthenticated && onboardingComplete) {
    return (
      <AuthShell
        headline={<>Start a new<br />customer signup.</>}
        sub="This affiliate link is ready. To test the customer flow from a signed-in dashboard, start a fresh signup session."
      >
        <div
          className="opacity-0 animate-fade-in-up"
          style={{ animationDelay: '80ms', animationFillMode: 'forwards', animationTimingFunction: EASE_OUT_STRONG }}
        >
          <h1 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight mb-3">
            Affiliate link captured
          </h1>
          <p className="text-white/60 text-sm leading-relaxed mb-6">
            The code has been saved for the next signup. Continue when you are ready to create a new customer account from this link.
          </p>
          {code && (
            <div className="mb-6 rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2 font-mono text-xs text-white/70 break-all">
              {code}
            </div>
          )}
          <div className="flex flex-col gap-3 sm:flex-row">
            <Button
              type="button"
              onClick={startCustomerSignup}
              className="h-11 bg-gradient-to-r from-brand-royal to-brand-periwinkle text-white"
            >
              <LogOut className="mr-2 h-4 w-4" />
              Start customer signup
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/')}
              className="h-11 border-white/15 bg-white/[0.03] text-white hover:bg-white/10 hover:text-white"
            >
              Stay signed in
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </AuthShell>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center">
      <LoadingSpinner size="lg" />
    </div>
  )
}
